import type { Currency, LineupItem, OnairSlot } from '@apm/contracts';

import type { EpgRow } from '../epg.js';
import { isFavorite } from '../epg.js';
import { onairNowIndex, onairSpansAt } from '../epg-grid.js';
import { watchNowLabel } from './watch-target.js';
import type { WatchNowLabel } from './watch-target.js';

/**
 * 홈 모델 (v32 · `docs/63` §4.3). **DOM 도 fetch 도 없다** — 입력을 받아 그릴 것을 낸다.
 *
 * 재료는 편성표와 **같은 행**(`toEpgRows` 의 `EpgRow[]`)이다. 그래서 절대 규칙 68 의 「첫 카드는
 * 그 채널을 켰을 때 실제로 나오는 것」이 홈에서도 성립한다 — 홈쇼핑 행은 `toEpgRows` 안에서
 * 이미 `rotateToProduct` 로 돌아 있고(규칙 70), 일반 행은 서버가 이어볼 회차를 맨 앞에 뒀다.
 *
 * **빈 레일은 여기 없다** — 화면이 `null` 을 내는 것이 아니라 모델에 애초에 없다(규칙 29 ·
 * 없는 것은 후보가 아니다).
 */
export interface ItemBrief {
  readonly productId: string;
  readonly title: string;
  readonly seasonNo: number | null;
  readonly episodeNo: number | null;
  readonly thumbnailUrl: string;
  /** 홈쇼핑 항목만 값이 있다. 일반·ONAIR 는 `null` */
  readonly price: number | null;
  readonly currency: Currency | null;
}

export interface MastheadInfo {
  readonly row: EpgRow;
  readonly now: ItemBrief | null;
  readonly next: readonly ItemBrief[];
  readonly label: WatchNowLabel;
  readonly poster: string | null;
}

export type MastheadModel =
  | ({ readonly kind: 'preview'; readonly url: string; readonly startSec: number } & MastheadInfo)
  | ({ readonly kind: 'poster' } & MastheadInfo)
  /**
   * 2단계 — 광고 소재의 **자리**(D3 · `docs/63` §5.7). 타입에만 있다. `composeHome` 이 이것을
   * 내는 분기는 없고 `compose-home.test.ts` 가 그것을 단언한다. 출처는 정하지 않았다(Q5).
   */
  | {
      readonly kind: 'creative';
      readonly imageUrl?: string;
      readonly videoUrl?: string;
      readonly clickTarget?: string;
      readonly impressionUrl?: string;
    }
  | { readonly kind: 'empty' };

export interface ChannelCard {
  readonly row: EpgRow;
  readonly now: ItemBrief | null;
}
export interface ItemCard {
  readonly channelId: string;
  readonly item: ItemBrief;
}

/**
 * 레일 셋 — 즐겨찾기 · 채널 · 지금 판매 중. **「바로 시청하기」 레일은 없다**(2026-09-10 실기 뒤
 * 걷었다) — masthead 의 CTA 가 같은 일을 하고 같은 문구라 두 번 보였다. 광고 소재가 masthead 에
 * 들어오는 날(D3) 대상 채널의 카드가 다시 필요해지면 그때 되돌린다.
 */
export type RailModel =
  | { readonly id: 'favorites'; readonly cards: readonly ChannelCard[] }
  | { readonly id: 'channels'; readonly cards: readonly ChannelCard[] }
  | { readonly id: 'selling'; readonly channelId: string; readonly cards: readonly ItemCard[] };

export interface HomeModel {
  readonly masthead: MastheadModel;
  readonly rails: readonly RailModel[];
}

/** 「다음」의 수 — masthead 의 세 줄 (`docs/63` §4.6) */
export const NEXT_COUNT = 3;

function briefOfItem(item: LineupItem, shopping: boolean): ItemBrief {
  return {
    productId: item.productId,
    title: item.title,
    seasonNo: item.content?.seasonNo ?? null,
    episodeNo: item.content?.episodeNo ?? null,
    thumbnailUrl: item.thumbnailUrl,
    price: shopping ? item.price : null,
    currency: shopping ? item.currency : null,
  };
}

function briefOfSlot(slot: OnairSlot): ItemBrief {
  return {
    productId: slot.productId,
    title: slot.title,
    seasonNo: slot.seasonNo,
    episodeNo: slot.episodeNo,
    thumbnailUrl: slot.thumbnailUrl,
    price: null,
    currency: null,
  };
}

/**
 * 「지금」과 「다음 셋」.
 *
 * - 개인화 행: `items[0]` 이 지금이고 `items[1..3]` 이 다음이다 — 회전은 이미 끝나 있다.
 * - ONAIR 행: **편성표와 같은 함수**(`onairSpansAt` · `onairNowIndex`)가 지금을 가리킨다
 *   (D8 · 홈이 따로 세면 편성표와 다른 「지금」이 생긴다). 편성은 한 바퀴라 다음은 감는다.
 */
export function nowAndNext(
  row: EpgRow,
  nowMs: number,
): { now: ItemBrief | null; next: ItemBrief[] } {
  if (row.channel.delivery === 'onair') {
    const slots = row.onair?.slots ?? [];
    if (slots.length === 0) return { now: null, next: [] };
    const at = onairNowIndex(onairSpansAt(slots, nowMs));
    if (at < 0) return { now: null, next: [] };
    const next: ItemBrief[] = [];
    for (let i = 1; i <= NEXT_COUNT && i < slots.length; i += 1) {
      const slot = slots[(at + i) % slots.length];
      if (slot !== undefined) next.push(briefOfSlot(slot));
    }
    return { now: briefOfSlot(slots[at] as OnairSlot), next };
  }
  const items = row.lineup?.items ?? [];
  const shopping = row.channel.kind === 'homeshopping';
  const [first, ...rest] = items;
  return {
    now: first === undefined ? null : briefOfItem(first, shopping),
    next: rest.slice(0, NEXT_COUNT).map((item) => briefOfItem(item, shopping)),
  };
}

export interface ComposeHomeInput {
  /** `toEpgRows(...)` — `GET /v1/channels` 순서 그대로 (절대 규칙 71 · 여기서 정렬하지 않는다) */
  readonly rows: readonly EpgRow[];
  /** `resolveWatchTarget(...)` */
  readonly target: EpgRow | null;
  /** `useFavoriteChannels` (v25) */
  readonly favorites: readonly string[];
  /** 프리뷰 주소 (`docs/63` §5.3). 없으면 포스터 */
  readonly previewUrl: string | null;
  /** `resume.positionSec` 또는 0 */
  readonly previewStartSec: number;
  /** `previewAllowed(...)` */
  readonly previewAllowed: boolean;
  /** ONAIR 행의 「지금」에 쓴다 */
  readonly nowMs: number;
}

export function composeHome(input: ComposeHomeInput): HomeModel {
  const { rows, target, favorites, nowMs } = input;
  if (rows.length === 0 || target === null) {
    return { masthead: { kind: 'empty' }, rails: [] };
  }

  const cardOf = (row: EpgRow): ChannelCard => ({ row, now: nowAndNext(row, nowMs).now });

  const { now, next } = nowAndNext(target, nowMs);
  const info: MastheadInfo = {
    row: target,
    now,
    next,
    label: watchNowLabel(target),
    poster: now?.thumbnailUrl ?? target.channel.thumbnailUrl,
  };
  const masthead: MastheadModel =
    input.previewAllowed && input.previewUrl !== null
      ? { kind: 'preview', url: input.previewUrl, startSec: input.previewStartSec, ...info }
      : { kind: 'poster', ...info };

  const rails: RailModel[] = [];

  // 즐겨찾기 — **번호순 그대로**(`rows` 의 순서). 토글은 편성표의 것이다(D9).
  const favoriteCards = rows
    .filter((row) => isFavorite(favorites, row.channel.channelId))
    .map(cardOf);
  if (favoriteCards.length > 0) rails.push({ id: 'favorites', cards: favoriteCards });

  // 채널 — 전부 · 입력 순서 그대로 (절대 규칙 71).
  rails.push({ id: 'channels', cards: rows.map(cardOf) });

  // 지금 판매 중 — 대상이 홈쇼핑 `receiver` 채널일 때만. 일반·ONAIR 이면 레일 자체가 없다.
  if (
    target.channel.kind === 'homeshopping' &&
    target.channel.delivery === 'receiver' &&
    target.lineup !== null &&
    target.lineup.items.length > 0
  ) {
    rails.push({
      id: 'selling',
      channelId: target.channel.channelId,
      cards: target.lineup.items.map((item) => ({
        channelId: target.channel.channelId,
        item: briefOfItem(item, true),
      })),
    });
  }

  return { masthead, rails };
}
